import React, { Fragment, useMemo } from "react";
import { useDidMount } from "react-hooks-lib";
import styled from "styled-components";
import { Match } from "@reach/router";
import { View, Text, Divider } from "./components";
import { graphql } from "react-apollo";
import { compose } from "recompose";
import tagAlbumsQuery from "./graphql/tagAlbumsQuery";
import { Header, MobileHeader } from "./App";
import Overview from "./components/Overview";
import ArtistsIndex from "./ArtistsIndex";
import friendlyUrl from "slug";
import { scrollDown } from "./App";
import { getProfessions } from "./helpers";

import professions, { addToHairMakeUp, excludeFromMenu } from "./professions";
import Footer from "./Footer";
import gql from "graphql-tag";
import { Helmet } from "react-helmet";

const Container = styled(View)`
  min-height: 200vh;
`;
// while constructing the index, search for the correct artist image
// or else: pick the first image from the Bio
const artistImageTagQuery = gql`
  query Tag($user: String!, $tagName: String!) {
    User: userByUsername(username: $user) {
      id
      name
      nickname: username
      slideTags(filter: { name: { equalTo: $tagName } }) {
        id
        name
        slides {
          id
          file: image {
            id
            secret
          }
          videoUrl
          width
          height
          title
          description
          album {
            id
            title
          }
        }
      }
    }
  }
`;

const ActiveProfession = ({ children }) => (
  <Match path={"/artists/:profession"}>
    {({ match }) => {
      const activeProfession = professions.find(
        (profession, index) =>
          (!match && index === 0) ||
          (match && match.profession === friendlyUrl(profession))
      );
      return children(activeProfession || professions[0]);
    }}
  </Match>
);

function Artists({ isWide, ...props }) {
  const data = props.tagAlbums;
  const artistImages = props.artistImages;
  const albums = data.loading ? null : data.allTags && data.allTags[0].albums;
  const artistsByCategory = useMemo(() => {
    if (!albums) return [];
    return professions
      .filter((profession) => !excludeFromMenu.includes(profession))
      .map((profession) => ({
        name: profession,
        artists: albums.filter((album) => {
          if (profession === "Talent") {
            return album.tags.find((tag) => tag.name === profession);
          } else {
            return (
              (album.tags.find(
                (tag) =>
                  profession === "Hair & Make Up" &&
                  addToHairMakeUp.includes(tag.name)
              ) ||
                album.tags.find((tag) => tag.name === profession)) &&
              !album.tags.find((tag) => tag.name === "Talent")
            );
          }
        }),
      }));
  }, [albums]);
  useDidMount(() => scrollDown());
  return (
    <ActiveProfession>
      {(activeProfession) => {
        return (
          <Container>
            <Helmet>
              <title>Artists • House Of Orange Agency • Amsterdam</title>
            </Helmet>
            {isWide ? (
              <Header artists />
            ) : (
              <MobileHeader
                items={artistsByCategory.map((category) => ({
                  to: `/artists/${friendlyUrl(category.name)}`,
                  label: category.name,
                }))}
              />
            )}
            <View style={{ minHeight: "100vmin" }}>
              {artistsByCategory
                .filter((profession) => profession.name === activeProfession)
                .map((category) => (
                  <Fragment key={category.name}>
                    {1 === 4 && (
                      <Text textStyle="headline" mt={4}>
                        {category.name}
                      </Text>
                    )}
                    <Overview
                      slides={category.artists.map((artist) => {
                        const artistName = artist.title
                          .split("Volkers")
                          .join("Völkers")
                          .split(" - ")[0];
                        const artistImage =
                          artistImages && artistImages[artistName];
                        const slide =
                          artistImage ||
                          (artist.slides.length > 0 && artist.slides[0]);

                        const byProfession = !!artist.tags.find(
                          (tag) => tag.name === "By Profession"
                        );
                        const to = `/artist/${artist.slug.split("-bio")[0]}${
                          activeProfession === "Make Up" ? `#make-up` : ""
                        }${
                          byProfession
                            ? `?profession=${friendlyUrl(
                                activeProfession.replace("&", "")
                              )}`
                            : ""
                        }`;
                        const profession = getProfessions(artist.tags)
                          .filter((tag) => tag !== "Talent")
                          .join(" - ");
                        // fetch all professions
                        const category = category && category.name;
                        return {
                          ...slide,
                          to,
                          artist: artistName,
                          category,
                          profession,
                          byProfession,
                        };
                      })}
                    />
                  </Fragment>
                ))}
            </View>
            {isWide && (
              <Fragment>
                <Divider mt={6} mb={1} />
                <Text textStyle="headline">Artists</Text>
                <Divider mt={1} />
                <ArtistsIndex />
              </Fragment>
            )}
            <Footer isWide={isWide} />
          </Container>
        );
      }}
    </ActiveProfession>
  );
}

const transformArtistImages = (data) => {
  if (!data.User) return null;
  const slides = data.User.slideTags[0].slides;
  const artistImages = slides.reduce((collect, slide) => {
    return { [slide.album.title.split(" - ")[0]]: slide, ...collect };
  }, {});
  return artistImages;
};

export default compose(
  graphql(tagAlbumsQuery, {
    name: "tagAlbums",
    options: () => {
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          user: process.env.REACT_APP_WHITESPACE_USER,
          tagName: "Bio",
        },
      };
    },
  }),
  graphql(artistImageTagQuery, {
    name: "artistImagesData",
    props: (props) => {
      if (!props.artistImagesData) return null;
      return { artistImages: transformArtistImages(props.artistImagesData) };
    },
    options: (props) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          user: process.env.REACT_APP_WHITESPACE_USER,
          tagName: "artist-image",
        },
      };
    },
  })
)(Artists);
